import { useCallback, useEffect, useMemo, useState } from "react";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";

import ConfirmDialog from "components/ConfirmDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController, setMessage } from "context";
import { LojaDataGrid, LojaPageContainer } from "../common";
import {
  atualizarMarca,
  criarMarca,
  deletarMarca,
  listarMarcas,
  type IMarca,
} from "services/lojaMarcas";
import MarcaFormDialog from "./components/MarcaFormDialog";
import {
  MARCAS_ACCENT,
  actionIconSx,
  emptyStateSx,
  fieldSx,
  gridSx,
  marcasToolbarSx,
  saveButtonSx,
  statusToggleSx,
  switchSx,
} from "./marcas.theme";

type StatusFilter = "" | "ativo" | "inativo";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "", label: "Todas" },
  { value: "ativo", label: "Ativas" },
  { value: "inativo", label: "Inativas" },
];

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? MARCAS_ACCENT : "text.primary"}
        sx={{ fontSize: { xs: "1.25rem", sm: "2rem" } }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function LojaMarcas() {
  const [, dispatch] = useMaterialUIController();
  const [rows, setRows] = useState<IMarca[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState<IMarca | null>(null);
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState<{ open: boolean; id?: number; nome?: string }>({
    open: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listarMarcas();
      setRows(Array.isArray(data) ? data : []);
    } catch (error: unknown) {
      const err = error as { message?: string };
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: err?.message ?? "Falha ao carregar marcas.",
      });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = useMemo(() => {
    const ativas = rows.filter((r) => r.ativo).length;
    return { total: rows.length, ativas, inativas: rows.length - ativas };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter === "ativo" && !r.ativo) return false;
      if (statusFilter === "inativo" && r.ativo) return false;
      if (!term) return true;
      return (
        r.nome.toLowerCase().includes(term) ||
        r.slug.toLowerCase().includes(term)
      );
    });
  }, [rows, search, statusFilter]);

  const isFiltered = Boolean(search.trim() || statusFilter);

  const openCreate = useCallback(() => {
    setEditing(null);
    setOpenModal(true);
  }, []);

  const openEdit = useCallback((marca: IMarca) => {
    setEditing(marca);
    setOpenModal(true);
  }, []);

  const handleSave = useCallback(
    async (form: { nome: string; ativo: boolean }) => {
      setSaving(true);
      try {
        if (editing) {
          await atualizarMarca(editing.id, { nome: form.nome, ativo: form.ativo });
          setMessage(dispatch, { open: true, severity: "success", text: "Marca atualizada." });
        } else {
          await criarMarca({ nome: form.nome, ativo: form.ativo });
          setMessage(dispatch, { open: true, severity: "success", text: "Marca criada." });
        }
        setOpenModal(false);
        await load();
      } catch (error: unknown) {
        const err = error as { response?: { data?: { error?: string } }; message?: string };
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: err?.response?.data?.error ?? err?.message ?? "Falha ao salvar.",
        });
      } finally {
        setSaving(false);
      }
    },
    [editing, dispatch, load],
  );

  const toggleAtivo = useCallback(
    async (marca: IMarca) => {
      setTogglingId(marca.id);
      try {
        await atualizarMarca(marca.id, { ativo: !marca.ativo });
        setRows((prev) =>
          prev.map((r) => (r.id === marca.id ? { ...r, ativo: !marca.ativo } : r)),
        );
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: marca.ativo ? "Marca desativada." : "Marca ativada.",
        });
      } catch (error: unknown) {
        const err = error as { response?: { data?: { error?: string } }; message?: string };
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: err?.response?.data?.error ?? err?.message ?? "Falha ao atualizar status.",
        });
      } finally {
        setTogglingId(null);
      }
    },
    [dispatch],
  );

  const handleDelete = useCallback(async () => {
    if (confirm.id == null) return;
    try {
      await deletarMarca(confirm.id);
      setMessage(dispatch, { open: true, severity: "success", text: "Marca excluída." });
      setConfirm({ open: false });
      await load();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: err?.response?.data?.error ?? err?.message ?? "Falha ao excluir.",
      });
    }
  }, [confirm.id, dispatch, load]);

  const columns: GridColDef<IMarca>[] = useMemo(
    () => [
      {
        field: "nome",
        headerName: "Marca",
        flex: 1,
        minWidth: 220,
        renderCell: (params: GridRenderCellParams<IMarca>) => (
          <Box minWidth={0}>
            <MDTypography variant="button" fontWeight="medium" noWrap>
              {params.row.nome}
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary" display="block" noWrap>
              {params.row.slug}
            </MDTypography>
          </Box>
        ),
      },
      {
        field: "ativo",
        headerName: "Status",
        width: 160,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<IMarca>) => (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Tooltip title={params.row.ativo ? "Desativar marca" : "Ativar marca"}>
              <Switch
                size="small"
                checked={params.row.ativo}
                disabled={togglingId === params.row.id}
                onChange={() => void toggleAtivo(params.row)}
                onClick={(e) => e.stopPropagation()}
                sx={switchSx}
              />
            </Tooltip>
            <Chip
              size="small"
              label={params.row.ativo ? "Ativa" : "Inativa"}
              sx={{
                height: 22,
                fontSize: "0.7rem",
                bgcolor: params.row.ativo ? "rgba(16, 185, 129, 0.12)" : "grey.200",
                color: params.row.ativo ? "success.dark" : "text.secondary",
                fontWeight: 600,
              }}
            />
          </MDBox>
        ),
      },
      {
        field: "actions",
        headerName: "Ações",
        width: 110,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams<IMarca>) => (
          <MDBox display="flex" justifyContent="center">
            <Tooltip title="Editar marca">
              <IconButton size="small" onClick={() => openEdit(params.row)} sx={actionIconSx}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir marca">
              <IconButton
                size="small"
                onClick={() =>
                  setConfirm({ open: true, id: params.row.id, nome: params.row.nome })
                }
                sx={{ color: "error.main" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      },
    ],
    [openEdit, toggleAtivo, togglingId],
  );

  return (
    <LojaPageContainer
      title="Marcas"
      subtitle="Cadastre as marcas dos produtos da loja. Marcas inativas ficam ocultas na seleção de produtos."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={openCreate}
          sx={saveButtonSx}
        >
          Nova marca
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={isFiltered ? "No filtro" : "Total cadastradas"}
            value={isFiltered ? filteredRows.length : stats.total}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Exibindo agora" value={filteredRows.length} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Ativas" value={stats.ativas} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Inativas" value={stats.inativas} />
        </Grid>
      </Grid>

      <MDBox sx={marcasToolbarSx}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SellOutlinedIcon sx={{ color: MARCAS_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {filteredRows.length} marca{filteredRows.length === 1 ? "" : "s"}
              {isFiltered ? " no filtro" : " cadastrada" + (filteredRows.length === 1 ? "" : "s")}
            </MDTypography>
          </Stack>

          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Status
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={statusFilter}
              onChange={(_, next: StatusFilter | null) => {
                if (next === null) return;
                setStatusFilter(next);
              }}
              sx={statusToggleSx}
            >
              {STATUS_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value || "all-status"} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          <FormControl variant="outlined" size="small" fullWidth sx={fieldSx}>
            <InputLabel shrink htmlFor="marcas-search">
              Buscar
            </InputLabel>
            <OutlinedInput
              id="marcas-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              label="Buscar"
              notched
              placeholder="Nome ou slug…"
              endAdornment={
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
            />
          </FormControl>
        </Stack>
      </MDBox>

      {!loading && filteredRows.length === 0 ? (
        <MDBox sx={emptyStateSx}>
          <SellOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            {isFiltered ? "Nenhuma marca no filtro" : "Nenhuma marca cadastrada"}
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary" mb={2}>
            {isFiltered
              ? "Ajuste a busca ou o filtro de status."
              : "Cadastre marcas para vinculá-las aos produtos da loja."}
          </MDTypography>
          {!isFiltered ? (
            <MDButton
              variant="gradient"
              color="dark"
              startIcon={<AddOutlinedIcon />}
              onClick={openCreate}
              sx={saveButtonSx}
            >
              Cadastrar primeira marca
            </MDButton>
          ) : null}
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={filteredRows}
          getRowId={(r) => r.id}
          columns={columns}
          loading={loading}
          sx={gridSx}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
        />
      )}

      <MarcaFormDialog
        open={openModal}
        editing={editing}
        saving={saving}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={confirm.open}
        title="Confirmar exclusão"
        message={`Excluir a marca "${confirm.nome ?? ""}" permanentemente? Produtos vinculados impedirão a exclusão.`}
        onClose={() => setConfirm({ open: false })}
        onConfirm={handleDelete}
      />
    </LojaPageContainer>
  );
}
